import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * Finance-only Revenue dashboard (Management → Revenue). The server runs the
 * Stripe sync as a background job and stores its snapshot; the portal merges
 * the subscriber overrides finance staff make here, then computes every MRR,
 * cash and calendar number itself (containers/finance/revenue). 403 unless
 * the caller is on the finance allowlist.
 *
 * Subscriber records keep the camelCase field names of the original
 * dashboard's ledger so its billing engine ports unchanged.
 */

export type RevenueSubStatus = 'active' | 'past_due' | 'churned';

/** A plan change: from `effectiveDate` on, each charge is `amountUSD`. */
export interface RevenueUpgrade {
  effectiveDate: string;
  plan: string;
  amountUSD: number;
}

/** lifetime: every bill in [startDate, endDate] (endDate null = forever).
 *  onetime: the bill in `month` (YYYY-MM). percent: `value`% off; flat:
 *  `value` off the USD charge. */
export interface RevenueDiscount {
  type: 'lifetime' | 'onetime';
  mode: 'percent' | 'flat';
  value: number;
  startDate?: string | null;
  endDate?: string | null;
  month?: string | null;
}

/** One subscriber. `amountUSD` (and each upgrade's) is ONE CHARGE, not MRR:
 *  a yearly plan's charge covers twelve months. */
export interface RevenueSubscriberRecord {
  stripeId?: string;
  customerId?: string;
  name: string;
  contact: string;
  email: string;
  amountUSD: number;
  plan: string;
  /** day | week | month | year */
  interval: string;
  intervalCount: number;
  billingDay: number;
  status: RevenueSubStatus;
  /** YYYY-MM-DD, Toronto. */
  startDate: string;
  churnDate: string | null;
  churnReason: string | null;
  upgrades: RevenueUpgrade[];
  discounts: RevenueDiscount[];
}

/** A one-off credit top-up (Checkout payment). `cad` is the settled CAD
 *  amount, null when it settled in USD; `refunded` is the refunded share. */
export interface RevenueTopup {
  id: string;
  pi: string;
  d: string;
  name: string;
  email: string;
  credits: number;
  amt: number;
  cur: string;
  cad: number | null;
  refunded: number;
}

/** One month of successful charges, net of refunds: CAD-settled one-off and
 *  recurring money, the USD-settled share kept apart, and the one-off count. */
export interface RevenueChargeMonth {
  one: number;
  rec: number;
  oneUsd: number;
  recUsd: number;
  n: number;
}

export interface RevenueCharges {
  /** Keyed YYYY-MM. */
  rev: Record<string, RevenueChargeMonth>;
  /** All-time paid per Stripe customer id. */
  spend: Record<string, { cad: number; usd: number }>;
}

/** Stripe net volume for a day, in cents, split by settlement currency. */
export interface RevenueNetvolDay {
  cad: number;
  usd: number;
}

export interface RevenueSnapshot {
  syncVersion: string;
  syncedAt: string;
  /** First day the net-volume map covers (YYYY-MM-DD). */
  netvolSince: string;
  errors: { topups?: string; charges?: string };
  counts: Record<string, number>;
  subscribers: RevenueSubscriberRecord[];
  topups: RevenueTopup[];
  charges: RevenueCharges;
  /** Keyed YYYY-MM-DD. */
  netvol: Record<string, RevenueNetvolDay>;
}

export type RevenueSyncState = 'idle' | 'running' | 'failed';

export interface RevenueSyncStatus {
  status: RevenueSyncState;
  progress: string | null;
  error: string | null;
  started_at: string | null;
  started_by: string | null;
  synced_at: string | null;
}

export type MrrEventType = 'new' | 'churn' | 'up' | 'down';

/** One MRR movement in CAD (`v` is negative for churn and downgrades). */
export interface MrrEvent {
  d: string;
  t: MrrEventType;
  v: number;
  n: string;
}

/** Stripe's "MRR growth detail" export, authoritative through `lastDate`. */
export interface RevenueHistory {
  events: MrrEvent[];
  lastDate: string;
  importedAt: string | null;
  importedBy: string | null;
}

export interface RevenueConfig {
  excludePatterns?: string[];
  churnFixes?: Record<string, string>;
}

/** A finance edit layered over the Stripe sync. `key` is the Stripe
 *  subscription id for a synced subscriber, `manual:<uuid>` for one added by
 *  hand (whose `data` is then the whole record). */
export interface RevenueOverride {
  key: string;
  data: Record<string, unknown>;
  deleted: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface RevenueResponse {
  snapshot: RevenueSnapshot | null;
  sync: RevenueSyncStatus;
  history: RevenueHistory | null;
  /** Stripe's monthly FX revaluation of the MRR base, CAD, keyed YYYY-MM. */
  fx_adj: Record<string, number> | null;
  config: RevenueConfig;
  overrides: RevenueOverride[];
}

export interface RevenueImportBody {
  history?: { events: MrrEvent[]; lastDate: string };
  fx_adj?: Record<string, number>;
  config?: RevenueConfig;
}

export interface RevenueOverrideBody {
  key: string;
  data: Record<string, unknown>;
  deleted: boolean;
}

const overridePath = (key: string) =>
  `/management/finance/revenue/overrides/${encodeURIComponent(key)}`;

export const adminRevenueApi = createApi({
  reducerPath: 'adminRevenueApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getRevenue: builder.query<RevenueResponse, void>({
      query: () => '/management/finance/revenue',
    }),
    /** Polled every 2s while a sync runs. */
    getRevenueSyncStatus: builder.query<RevenueSyncStatus, void>({
      query: () => '/management/finance/revenue/sync',
    }),
    /** Starts a background sync (a no-op when one is running). */
    startRevenueSync: builder.mutation<RevenueSyncStatus, void>({
      query: () => ({
        url: '/management/finance/revenue/sync',
        method: 'POST',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminRevenueApi.util.updateQueryData(
              'getRevenue',
              undefined,
              (draft) => {
                draft.sync = data;
              }
            )
          );
        } catch {
          // the caller toasts the error
        }
      },
    }),
    putRevenueOverride: builder.mutation<RevenueOverride, RevenueOverrideBody>({
      query: ({ key, data, deleted }) => ({
        url: overridePath(key),
        method: 'PUT',
        body: { data, deleted },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: saved } = await queryFulfilled;
          dispatch(
            adminRevenueApi.util.updateQueryData(
              'getRevenue',
              undefined,
              (draft) => {
                const i = draft.overrides.findIndex((o) => o.key === saved.key);
                if (i >= 0) draft.overrides[i] = saved;
                else draft.overrides.push(saved);
              }
            )
          );
        } catch {
          // the caller toasts the error
        }
      },
    }),
    deleteRevenueOverride: builder.mutation<void, string>({
      query: (key) => ({ url: overridePath(key), method: 'DELETE' }),
      async onQueryStarted(key, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            adminRevenueApi.util.updateQueryData(
              'getRevenue',
              undefined,
              (draft) => {
                draft.overrides = draft.overrides.filter((o) => o.key !== key);
              }
            )
          );
        } catch {
          // the caller toasts the error
        }
      },
    }),
    importRevenue: builder.mutation<RevenueResponse, RevenueImportBody>({
      query: (body) => ({
        url: '/management/finance/revenue/import',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminRevenueApi.util.upsertQueryData('getRevenue', undefined, data)
          );
        } catch {
          // the caller toasts the error
        }
      },
    }),
  }),
});

export const {
  useGetRevenueQuery,
  useGetRevenueSyncStatusQuery,
  useStartRevenueSyncMutation,
  usePutRevenueOverrideMutation,
  useDeleteRevenueOverrideMutation,
  useImportRevenueMutation,
} = adminRevenueApi;
